// Как сценарий называется человеку. SMY-60 · экраны 02, 03, 04
//
// Копирайт из design/README.md, собранный в одну таблицу. Разные экраны
// называют одно и то же по-разному, и это не небрежность: на карточке нужно
// короткое имя («Найти»), на шите подтверждения — обещание от первого лица
// («Найду и покажу источники со ссылками и датой»), на кнопке — действие
// («Начать поиск»).
//
// Живёт на сервере и приезжает клиенту, а не зашито в вёрстку: сценарий
// выключается из админки без релиза (SMY-32), и текст про него должен уметь
// приезжать оттуда же.
//
// ⚠️ Дизайн даёт `intro` и `ask` только для проверки фактов — сценария, которого
// с 06.09.2026 нет (см. ниже). Оставшиеся шесть написаны здесь, в том же
// голосе: обращение на «ты», без названий моделей, обещание конкретного
// результата. Их стоит перечитать вместе с продуктом.
//
// ⚠️ Сценарий «Проверить» убран целиком 06.09.2026. Формат — утверждения со
// статусами «подтверждено / спорно / не найдено» — оказался непонятным: люди
// не отличают его от обычного ответа со ссылками и не понимают, чем «спорно»
// отличается от «не найдено». Такие запросы пошли поиском.
//
// ⚠️ В тот же день следом за ней ушёл и сам сценарий «Найти» — по той же
// причине и на том же круге. Карточка со сводом, бейджем уверенности и
// списком расхождений оказалась не ответом, а пересказом выдачи: на «сколько
// стоит Wilson Blade 10 Pro» она отвечала «конкретная цена в источниках не
// указана» — то есть отчётом о поиске вместо результата.
//
// Хуже был не первый ответ, а второй. У карточки была бесплатная правка, и
// правка карточки поиска НЕ ИСКАЛА заново: она переписывала свод теми же
// словами по тем же источникам. Три бесплатных уточнения подряд возвращали
// один и тот же текст, повёрнутый другим боком, — и человек оставался в
// разговоре, из которого нет выхода к ответу.
//
// Веб-поиск при этом никуда не делся: он перестал быть сценарием и стал
// ЭТАПОМ внутри обычного ответа. На тот же вопрос продукт теперь отвечает
// текстом, а найденные ссылки показывает под запуском. Уточнения идут
// бесплатным продолжением разговора (FREE_FOLLOWUPS), то есть настоящим
// новым ответом, а не правкой старого.

import {
  FREE_FOLLOWUPS, formatSize,
  type EditPolicy, type ScenarioSpec, type UnitId, type UnitScope,
} from './units.ts'
import type { ScenarioId } from './config.ts'

export interface ScenarioCopy {
  /** Название карточки на экране 02. */
  card: string
  /** Подсказка под названием — что получится. */
  hint: string
  /** Метка капсом на шите подтверждения. */
  label: string
  /** Обещание в заголовке шита, от первого лица. */
  promise: string
  /** Основная кнопка шита. */
  action: string
  /**
   * Отказ от дорогой единицы: то же самое обычным ответом и без списания.
   *
   * Строкой на сценарий, а не собранное из падежей: «Ответить без разбора» и
   * «Ответить без поиска» — разные падежные формы разных слов, и вывести их
   * из названия карточки нельзя.
   */
  decline: string
  /**
   * Что я сделаю — для ответа ассистента на экране 03, от первого лица и
   * без заглавной: строка встраивается в «Похоже, это поиск с источниками: …».
   */
  intro: string
  /**
   * Чего я жду — приветствие ассистента на экране 03б, когда сценарий выбран
   * руками и спрашивать нечего.
   */
  ask: string
}

export const SCENARIO_COPY: Record<ScenarioId, ScenarioCopy> = {
  // Карточка одна на всё, что списывается вопросом: с уходом «Найти» поиск
  // стал частью этого же ответа, и подсказка обязана это назвать — иначе
  // человек, пришедший за свежей ценой, не найдёт на экране 02 ничего своего.
  chat: {
    card: 'Спросить',
    hint: 'Ответ на вопрос, при нужде — со свежими ссылками',
    label: 'ВОПРОС',
    promise: 'Отвечу на вопрос',
    action: 'Ответить',
    decline: 'Ответить коротко',
    intro: 'отвечу по делу, а за свежим — схожу в интернет',
    ask: 'Что подсказать? Спрашивай обычными словами — отвечу по делу, а за свежим схожу в интернет.',
  },
  write: {
    card: 'Сгенерировать текст',
    hint: 'Письмо, сообщение, объявление, резюме',
    label: 'ТЕКСТ',
    promise: 'Напишу готовый к отправке текст',
    action: 'Написать',
    decline: 'Ответить коротко',
    intro: 'напишу так, чтобы можно было отправить не редактируя',
    ask: 'Что написать? Скажи, кому и зачем — верну готовый к отправке текст.',
  },
  doc_review: {
    card: 'Разобрать документ',
    hint: 'Резюме, риски, вопросы и правки',
    label: 'РАЗБОР ДОКУМЕНТА',
    promise: 'Разберу документ: резюме, риски и что упущено',
    action: 'Разобрать',
    decline: 'Ответить без разбора — бесплатно',
    intro: 'покажу резюме, риски по пунктам и что упущено',
    ask: 'Какой документ разобрать? Загрузи файл — верну резюме, риски по пунктам и пропуски.',
  },
  doc_create: {
    card: 'Создать документ',
    hint: 'Претензия, заявление, шаблон, план',
    label: 'СОЗДАНИЕ ДОКУМЕНТА',
    promise: 'Соберу документ по шаблону и проверю полноту',
    action: 'Создать документ',
    decline: 'Ответить текстом — бесплатно',
    intro: 'соберу его по шаблону и проверю, всё ли на месте',
    ask: 'Какой документ собрать? Опиши ситуацию — соберу по шаблону и скажу, чего не хватает.',
  },
  image: {
    card: 'Создать изображение',
    hint: 'Карточка товара, обложка, иллюстрация',
    label: 'ИЗОБРАЖЕНИЕ',
    promise: 'Нарисую изображение по описанию',
    action: 'Нарисовать',
    decline: 'Описать словами — бесплатно',
    intro: 'нарисую по описанию и отдам файлом',
    ask: 'Что нарисовать? Опиши картинку словами — верну изображение.',
  },
}

/**
 * Подпись повтора сбойного запуска — действие сценария и «ещё раз». SMY-352
 *
 * «Повторить» и «Запустить ещё раз» называли механизм, а не результат, и
 * читались техническим словом; действие сценария называет, что человек
 * получит, — «Нарисовать ещё раз», «Создать документ ещё раз». Те же глаголы,
 * что на кнопке подтверждения. Повтор стоит ровно как первая попытка: сбой
 * запас не тратит (`docs/billing.md`). Тексты — `docs/copy/chat-recovery.md`.
 */
export const againLabel = (scenario: ScenarioId): string => `${SCENARIO_COPY[scenario].action} ещё раз`

/**
 * Строка ассистента на экране 03: что я понял и сколько уточню.
 *
 * Собирается, а не лежит готовой, потому что число вопросов приходит от
 * классификатора: «Уточню один момент» и «Уточню три момента» — разные
 * падежи, и склонять их на экране значило бы держать склонение в вёрстке.
 */
export function clarifyLine(scenario: ScenarioId, questions: number): string {
  const copy = SCENARIO_COPY[scenario]
  const what = `Похоже, это ${copy.label.toLowerCase()}: ${copy.intro}.`
  if (questions <= 0) return what
  const WORDS = ['', 'один момент', 'два момента', 'три момента']
  return `${what} Уточню ${WORDS[questions] ?? `${questions} момента`}.`
}

/**
 * Состав результата и момент списания — то, что показывает экран 04.
 *
 * Ключом СЦЕНАРИЙ, а не единица, с 06.09.2026. Пока вопрос и поиск были
 * разными единицами, спецификация единицы и описывала работу; теперь единица
 * у обоих одна, а обещания разные — «до 7 источников со ссылками» из слова
 * «вопрос» не выведешь.
 *
 * Строки собираются из чисел, а не лежат готовыми. Причина прямая: числа
 * меняются из админки после беты (SMY-89), и застывший текст «До 7
 * источников» пережил бы смену объёма на 5, не изменившись. Обещание,
 * разошедшееся с поведением, хуже отсутствующего.
 */
export function scenarioSpec(
  scenario: ScenarioId,
  unit: UnitId,
  scope: UnitScope,
  edits: EditPolicy,
  /**
   * Сколько источников читает веб-поиск внутри ответа.
   *
   * Аргументом, а не из `scope`: объём результата чата — один ответ, а
   * источники это то, чем он собран. Одной величиной на сценарий их не
   * выразить, и `UnitScope` этого и не пытается (см. units.ts).
   */
  webSources: number,
): ScenarioSpec {
  const followUps = `Ещё ${FREE_FOLLOWUPS} уточнения в этом чате — без списания`
  // Обещание собирается из политики, а не пишется словами: у изображения это
  // минуты, у документа — число правок, и одной фразой их не назвать.
  const editLine =
    edits.minutes != null
      ? `Правка в течение ${edits.minutes} минут — без списания`
      : `${edits.count} правки этого результата — без списания`
  const includes: Record<ScenarioId, string[]> = {
    // Вторая строка — обещание условное, и сказано это словами: продукт идёт
    // в интернет не на каждый вопрос, а когда ответ зависит от «сейчас».
    // Безусловное «до 7 источников» обещало бы ссылки под «почему небо
    // голубое» и разошлось бы с поведением на самом частом запросе.
    chat: [
      'Один ответ на вопрос',
      `До ${formatSize({ size: webSources, kind: 'sources' })} со ссылками, когда нужны свежие сведения`,
      followUps,
    ],
    write: ['Готовый к отправке текст', followUps],
    doc_review: [`Разбор документа до ${formatSize(scope)}`, 'Риски, пропуски и поправки', editLine],
    doc_create: [`Один документ до ${formatSize(scope)}`, 'Экспорт в DOCX или PDF', editLine],
    image: [`Один запрос и ${formatSize(scope)}`, 'Быстрая правка без нового списания', editLine],
  }
  const chargedWhen: Record<ScenarioId, string> = {
    chat: 'После выдачи содержательного ответа',
    write: 'После выдачи готового текста',
    doc_review: 'После готового разбора',
    doc_create: 'После создания редактируемого файла',
    image: 'После получения вариантов',
  }
  return { unit, includes: includes[scenario], chargedWhen: chargedWhen[scenario] }
}
